import { create } from 'zustand'
import BigNumber from 'bignumber.js'

// REST 응답 형태로 통일해서 보관 (WS 데이터도 동일 형태로 변환)
type StoredAssets = AccountAssetsResponse['assets']

interface UserAccState {
  // assets — REST/WS 이중 보관. selector는 ws 우선 반환
  restAssets: StoredAssets | null
  wsAssets: StoredAssets | null

  // positions — REST/WS 이중 보관
  restPositions: Position[]
  wsPositions: Position[] | null // null = WS 미수신, []는 빈 배열로 명시적 수신

  // openOrders — REST/WS 이중 보관
  restOpenOrders: OpenOrder[]
  wsOpenOrders: OpenOrder[] | null

  // WS 전용 (REST 대응 없음)
  tpEvents: UserAccTpEvent[]
  tpStatus: TpStatus[]

  setFromRestAssets: (assets: StoredAssets) => void
  setFromRestPositions: (positions: Position[]) => void
  setFromRestOpenOrders: (orders: OpenOrder[]) => void

  applyWsAssets: (msg: UserAccAssetsMsg) => void
  applyWsPositions: (msg: UserAccPositionsMsg) => void
  applyWsOpenOrders: (msg: UserAccOpenOrdersMsg) => void
  appendTpEvents: (events: UserAccTpEvent[]) => void
  setTpStatus: (statuses: UserAccTpStatusItem[]) => void

  reset: () => void
}

// 2101 — WS 자산 push를 REST 필드로 매핑
// 서버는 datas 키 + number 타입으로 보내므로 String() 변환
const mapWsAssetsToRest = (msg: UserAccAssetsMsg, prev: StoredAssets | null): StoredAssets => {
  const a = msg.datas
  const base: StoredAssets =
    prev ??
    ({
      balance: '0',
      equity: '0',
      profit: '0',
      margin: '0',
      freeMargin: '0',
      marginLevel: '0',
      accountLogin: 0,
      accountId: '',
      leverage: '0',
      credit: '0',
      marginSoCall: '0',
      marginStopOut: '0',
      marginInitial: '0',
      marginMaintenance: '0',
      assets: '0',
      marginCall: '0',
      marginStopout: '0',
      marginFree: '0',
      marginBlocked: '0',
      accountCredit: '0',
      accountAssets: '0',
      accountLiabilities: '0',
    } as StoredAssets)

  return {
    ...base,
    balance: String(a.bal),
    equity: String(a.eq),
    profit: String(a.pf),
    margin: String(a.mg),
    freeMargin: String(a.fm),
    marginFree: String(a.fm),
    marginLevel: String(a.ml),
    leverage: String(a.lev),
    credit: String(a.cr),
    marginInitial: String(a.mi),
    marginMaintenance: String(a.mmn),
    marginSoCall: String(a.msc),
    marginStopOut: String(a.mso),
    assets: String(a.as),
    accountId: String(a.acc),
  }
}

// 2102 WS 포지션 → REST Position 머지
const mergeWsPositionToRest = (prev: Position | undefined, item: UserAccPositionItem): Position => {
  const base: Position =
    prev ??
    ({
      curPrice: '0',
      openPrice: '0',
      openTimeMsc: 0,
      positionId: Number(item.pid),
      positionIdentifier: Number(item.id),
      positionMagic: Number(item.m),
      positionType: item.pt,
      profit: '0',
      side: item.pt === 0 ? 'BUY' : 'SELL',
      stopLoss: '0',
      swap: '0',
      symbol: item.sym,
      takeProfit: '0',
      updateMode: item.um,
      volume: '0',
    } as Position)

  return {
    ...base,
    symbol: item.sym,
    positionId: Number(item.pid),
    positionIdentifier: Number(item.id),
    positionMagic: Number(item.m),
    positionType: item.pt,
    side: item.pt === 0 ? 'BUY' : 'SELL',
    openTimeMsc: Number(item.t),
    volume: item.v,
    openPrice: item.po,
    curPrice: item.pc,
    stopLoss: item.sl,
    takeProfit: item.tp,
    profit: item.pf,
    swap: item.sw,
    updateMode: item.um,
  }
}

// 2103 WS 미체결 주문 → REST OpenOrder 머지
const mergeWsOpenOrderToRest = (
  prev: OpenOrder | undefined,
  item: UserAccOpenOrderItem
): OpenOrder => {
  const side: 'BUY' | 'SELL' = item.ot % 2 === 0 ? 'BUY' : 'SELL'
  const base: OpenOrder =
    prev ??
    ({
      ticket: Number(item.o),
      symbol: item.sym,
      accountId: '',
      type: String(item.ot),
      side,
      state: String(item.os),
      timeSetup: item.ts,
      timeExpiration: item.exp,
      timeDone: item.td,
      expiration: 0,
      volumeInit: '0',
      volumeCurr: '0',
      priceOpen: '0',
      stopLoss: '0',
      takeProfit: '0',
      priceCurrent: '0',
      priceStopLimit: '0',
      comment: item.cmt,
      createdAt: '',
      orderTypeFilling: item.tf,
      orderTypeTime: item.tt,
      orderReason: item.or,
      orderMagic: Number(item.m),
      orderPositionId: Number(item.pid),
      orderPositionById: Number(item.pbid),
      updateMode: item.um,
      orderExternalId: item.eid,
    } as OpenOrder)

  return {
    ...base,
    ticket: Number(item.o),
    symbol: item.sym,
    type: String(item.ot),
    side,
    state: String(item.os),
    timeSetup: item.ts,
    timeExpiration: item.exp,
    timeDone: item.td,
    volumeInit: item.vi,
    volumeCurr: item.vc,
    priceOpen: item.po,
    priceStopLimit: item.psl,
    priceCurrent: item.pc,
    stopLoss: item.sl,
    takeProfit: item.tp,
    comment: item.cmt,
    orderTypeFilling: item.tf,
    orderTypeTime: item.tt,
    orderReason: item.or,
    orderMagic: Number(item.m),
    orderPositionId: Number(item.pid),
    orderPositionById: Number(item.pbid),
    updateMode: item.um,
    orderExternalId: item.eid,
  }
}

// REST positions를 string 정규화
const normalizeRestPositions = (positions: Position[]): Position[] =>
  positions.map(p => ({
    ...p,
    openPrice: String(p.openPrice),
    curPrice: String(p.curPrice),
    volume: String(p.volume),
    profit: String(p.profit),
    swap: String(p.swap),
    stopLoss: String(p.stopLoss),
    takeProfit: String(p.takeProfit),
  }))

// 평균가 TP 원본(number) → store 정규화(string). REST avgtp/WS 2105 공통 진입점
const normalizeTpStatus = (item: UserAccTpStatusItem): TpStatus => ({
  st: item.st,
  sym: item.sym,
  sd: item.sd,
  co: item.co,
  tp: new BigNumber(item.tp).toString(),
  m: new BigNumber(item.m).toString(),
  v: new BigNumber(item.v).toString(),
  aop: new BigNumber(item.aop).toString(),
  tppr: new BigNumber(item.tppr).toString(),
})

// REST openOrders를 string 정규화
const normalizeRestOpenOrders = (orders: OpenOrder[]): OpenOrder[] =>
  orders.map(o => ({
    ...o,
    priceOpen: String(o.priceOpen),
    priceCurrent: String(o.priceCurrent),
    priceStopLimit: String(o.priceStopLimit),
    volumeInit: String(o.volumeInit),
    volumeCurr: String(o.volumeCurr),
    stopLoss: String(o.stopLoss),
    takeProfit: String(o.takeProfit),
  }))

// Selector helpers — REST/WS 머지 (ws 우선)
// 사용: const assets = useUserAccStore(selectAssets)
export const selectAssets = (s: UserAccState): StoredAssets | null => s.wsAssets ?? s.restAssets
// WS를 한 번이라도 수신했으면(null이 아니면) WS를 신뢰한다.
// 빈 배열([])은 "모든 포지션이 닫혀 비었음"을 의미하므로 rest로 폴백하지 않는다.
// (applyWsPositions는 첫 push에서 rest를 복사한 뒤 머지하므로 빈 배열로 굳지 않음)
export const selectPositions = (s: UserAccState): Position[] =>
  s.wsPositions !== null ? s.wsPositions : s.restPositions
export const selectOpenOrders = (s: UserAccState): OpenOrder[] =>
  s.wsOpenOrders !== null ? s.wsOpenOrders : s.restOpenOrders

export const useUserAccStore = create<UserAccState>()(set => ({
  restAssets: null,
  wsAssets: null,
  restPositions: [],
  wsPositions: null,
  restOpenOrders: [],
  wsOpenOrders: null,
  tpEvents: [],
  tpStatus: [],

  setFromRestAssets: assets => set({ restAssets: assets }),
  setFromRestPositions: positions =>
    set({ restPositions: normalizeRestPositions(positions) }),
  setFromRestOpenOrders: orders =>
    set({ restOpenOrders: normalizeRestOpenOrders(orders) }),

  applyWsAssets: msg => set(state => ({ wsAssets: mapWsAssetsToRest(msg, state.wsAssets) })),

  applyWsPositions: msg =>
    set(state => {
      // 매칭 키: pid (백엔드 확인 시 변경 가능)
      let next = (state.wsPositions ?? state.restPositions).slice()
      for (const item of msg.datas) {
        const pid = Number(item.pid)
        const idx = next.findIndex(p => p.positionId === pid)
        if (item.um === 1) {
          // 추가
          if (idx === -1) next.push(mergeWsPositionToRest(undefined, item))
          else next[idx] = mergeWsPositionToRest(next[idx], item)
        } else if (item.um === 2) {
          // 수정
          if (idx >= 0) next[idx] = mergeWsPositionToRest(next[idx], item)
          else next.push(mergeWsPositionToRest(undefined, item))
        } else if (item.um === 3) {
          // 삭제
          if (idx >= 0) next = next.filter((_, i) => i !== idx)
        }
        // um === 0 — 변경없음, 무시
      }
      return { wsPositions: next }
    }),

  applyWsOpenOrders: msg =>
    set(state => {
      // 매칭 키: o (백엔드 확인 시 변경 가능)
      let next = (state.wsOpenOrders ?? state.restOpenOrders).slice()
      for (const item of msg.datas) {
        const ticket = Number(item.o)
        const idx = next.findIndex(o => o.ticket === ticket)
        if (item.um === 1) {
          if (idx === -1) next.push(mergeWsOpenOrderToRest(undefined, item))
          else next[idx] = mergeWsOpenOrderToRest(next[idx], item)
        } else if (item.um === 2) {
          if (idx >= 0) next[idx] = mergeWsOpenOrderToRest(next[idx], item)
          else next.push(mergeWsOpenOrderToRest(undefined, item))
        } else if (item.um === 3) {
          if (idx >= 0) next = next.filter((_, i) => i !== idx)
        }
        // um === 0 무시
      }
      return { wsOpenOrders: next }
    }),

  appendTpEvents: events => set(state => ({ tpEvents: state.tpEvents.concat(events) })),

  setTpStatus: statuses => set({ tpStatus: statuses.map(normalizeTpStatus) }),

  reset: () =>
    set({
      restAssets: null,
      wsAssets: null,
      restPositions: [],
      wsPositions: null,
      restOpenOrders: [],
      wsOpenOrders: null,
      tpEvents: [],
      tpStatus: [],
    }),
}))
